import { FieldArray, Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../../components/Spin";
import { Label } from "../../../../components/ui/label";
import CustomLabelText from "../../../../components/CustomLabelText";
import { Input } from "../../../../components/ui/input";
import { API, fileUpload, get } from "../../../../config";
import { Button } from "../../../../components/ui/button";
import {
  AdminAssessmentSchema,
  AdminTrainingSchema,
} from "../../../../lib/Validation";
import { Textarea } from "../../../../components/ui/textarea";
import SearchSelect from "../../../../components/SearchSelect";
import { Loader2, Plus, Trash2 } from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../../../../components/ui/avatar";
import { useAllOrganizationTypes } from "../../../Auth/SignUpHelper";
import { MultiSelect } from "../../../../components/SearchMultiSelect";

const AddEditTraining = ({
  id,
  toggleShowAddEditTraining,
  edit,
  assessment = false,
  fetchTrainingData,
}) => {
  const [trainingData, setTrainingData] = useState([]);
  const [loading, setLoading] = useState(false);

  const { allOrganizationTypeLoading, allOrganizationType = [] } =
    useAllOrganizationTypes({ amlCTFType: true });
  const organizationType = allOrganizationType?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name, value, ...rest };
  });

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.ADMIN_TRAINING_DETAILS}/` + id);
      setTrainingData(reponse?.data?.data);
      setLoading(false);
    } catch (e) {
      setTrainingData(null);
    }
  }

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFormSubmit = async (values) => {
    const formData = new FormData();

    // --- Simple Fields ---
    if (values.banner_image) {
      formData.append("banner_image", values.banner_image); // must be File or Blob
    }

    values.organization_type_ids.forEach((org, orgIndex) => {
      formData.append(`organization_type_ids[${orgIndex}]`, org ? org : "");
    });

    if (!assessment) {
      formData.append("order", values.order ?? "");
      formData.append("link", values.link ?? "");
    }
    formData.append("title", values.title ?? "");
    formData.append("description", values.description ?? "");
    if (assessment) {
      formData.append("assessment", 1);
    }

    // --- Questions Array ---
    values.questions.forEach((q, qIndex) => {
      formData.append(`questions[${qIndex}][id]`, q?.id ? q?.id : "");
      formData.append(
        `questions[${qIndex}][training_id]`,
        q?.training_id ? q?.training_id : "",
      );
      formData.append(`questions[${qIndex}][question]`, q.question);
      // formData.append(`questions[${qIndex}][question]`, q.question);
      formData.append(`questions[${qIndex}][type]`, q.type);
      formData.append(`questions[${qIndex}][answer]`, q.answer);

      // Options (nested)
      q.options.forEach((opt, optIndex) => {
        formData.append(
          `questions[${qIndex}][options][${optIndex}][id]`,
          opt.id ? opt?.id : "",
        );
        formData.append(
          `questions[${qIndex}][options][${optIndex}][label]`,
          opt.label,
        );
        formData.append(
          `questions[${qIndex}][options][${optIndex}][text]`,
          opt.text,
        );
        formData.append(
          `questions[${qIndex}][options][${optIndex}][order]`,
          opt.order,
        );
      });
    });

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.ADMIN_TRAINING_ADD}${edit ? `/${id}` : ""}`,
        formData,
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditTraining();
        fetchTrainingData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{
        organization_type_ids: trainingData?.organization_type_ids,
        banner_image: trainingData?.banner_image,
        imagePreview: trainingData?.imagePreview,
        order: trainingData?.order,
        title: trainingData?.title,
        description: trainingData?.description,
        link: trainingData?.link,
        questions:
          trainingData?.questions && trainingData?.questions?.length > 0
            ? trainingData?.questions
            : [
                {
                  question: "",
                  type: "",
                  answer: "",
                  options: [],
                },
              ],
      }}
      validationSchema={
        assessment ? AdminAssessmentSchema : AdminTrainingSchema
      }
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setTouched,
        validateField,
        isValid,
      }) => (
        <>
          {loading && <Spin />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-question md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg">
              {/* IMAGE UPLOAD */}
              <div className="grid gap-4">
                {/* {!assessment && ( */}
                <div className="grid gap-2">
                  <Label>
                    <CustomLabelText
                      required={assessment ? false : true}
                      labelText="Training Image"
                      errorField={errors?.banner_image}
                      bottomAlign={true}
                    />
                  </Label>
                  <div className="border rounded-lg p-4 flex items-center justify-center bg-gray-50 h-60 relative">
                    <Avatar className="h-full w-full">
                      {values?.banner_image && (
                        <AvatarImage
                          src={
                            values?.imagePreview
                              ? values?.imagePreview
                              : values?.banner_image
                                ? values?.banner_image
                                : ""
                          }
                          className="h-52 object-contain"
                        />
                      )}
                      <AvatarFallback className="rounded-lg">
                        {"Add Picture"}
                      </AvatarFallback>
                    </Avatar>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (setFieldValue && file) {
                          setFieldValue("banner_image", file);
                          const previewUrl = URL.createObjectURL(file);
                          setFieldValue("imagePreview", previewUrl);
                          setTimeout(() => {
                            setTouched({ banner_image: true });
                            validateField("banner_image");
                          }, 0);
                        }
                      }}
                      className="absolute w-full h-full opacity-0 cursor-pointer p-1"
                    />
                  </div>
                  {errors?.banner_image && (
                    <p className="text-red-500 text-sm">
                      {errors.banner_image}
                    </p>
                  )}
                </div>
                {/* )} */}

                <div className="grid gap-2">
                  <Label htmlFor="type">
                    <CustomLabelText
                      required={true}
                      labelText="Industry Type"
                      errorField={errors?.organization_type_ids}
                      bottomAlign={true}
                    />
                  </Label>
                  <div className="relative">
                    <MultiSelect
                      options={organizationType?.length > 0 ? organizationType : []}
                      placeholder={"Industry Type"}
                      onValueChange={(e) => {
                        setFieldValue(`organization_type_ids`, e);
                      }}
                      handleBlur={handleBlur}
                      className={
                        errors?.organization_type_ids &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                      variant="inverted"
                      animation={2}
                      maxCount={1}
                      defaultValue={values?.organization_type_ids}
                    />
                    {allOrganizationTypeLoading && (
                      <span className="absolute top-2 right-2 animate-spin">
                        <Loader2 width={20} height={20} />
                      </span>
                    )}
                  </div>
                </div>

                <div
                  className={
                    !assessment
                      ? "grid gap-2 md:grid-cols-5"
                      : "grid gap-2 md:grid-cols-4"
                  }
                >
                  {/* ORDER */}

                  {!assessment && (
                    <div className="grid gap-2">
                      <Label htmlFor="order">
                        <CustomLabelText
                          required={true}
                          labelText="Order"
                          errorField={errors?.order}
                        />
                      </Label>

                      <Input
                        id="order"
                        type="number"
                        name="order"
                        min={1} // 🚫 no 0 or negatives
                        step={1}
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={values?.order || ""}
                        onKeyDown={(e) => {
                          // block dot, minus, exponent
                          if ([".", "-", "e", "E"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          const value = e.target.value;

                          // allow empty while typing
                          if (value === "") {
                            handleChange(e);
                            return;
                          }

                          // only whole numbers greater than 0
                          if (/^[1-9]\d*$/.test(value)) {
                            handleChange(e);
                          }
                        }}
                        onBlur={handleBlur}
                        autoComplete="off"
                        className={
                          errors?.order
                            ? "border-red-500 focus-visible:ring-red-500"
                            : ""
                        }
                      />
                    </div>
                  )}

                  {/* TITLE */}
                  <div className="grid gap-2 md:col-span-4">
                    <Label htmlFor="title">
                      <CustomLabelText
                        required={true}
                        labelText="Title"
                        errorField={errors?.title}
                      />
                    </Label>
                    <Input
                      id="title"
                      name="title"
                      value={values.title}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={
                        errors?.title
                          ? "border-red-500 focus-visible:ring-red-500"
                          : ""
                      }
                    />
                  </div>
                </div>

                {/* DESCRIPTION */}
                <div className="grid gap-2">
                  <Label htmlFor="description">
                    <CustomLabelText
                      required={true}
                      labelText="Description"
                      errorField={errors?.description}
                    />
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={values.description}
                    rows={3}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={
                      errors?.description
                        ? "border-red-500 focus-visible:ring-red-500"
                        : ""
                    }
                  />
                </div>

                {/* LINK */}
                {!assessment && (
                  <div className="grid gap-2">
                    <Label htmlFor="link">
                      <CustomLabelText
                        required={true}
                        labelText="Link"
                        errorField={errors?.link}
                      />
                    </Label>
                    <Input
                      id="link"
                      type="url"
                      name="link"
                      value={values.link}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={
                        errors?.link
                          ? "border-red-500 focus-visible:ring-red-500"
                          : ""
                      }
                    />
                  </div>
                )}

                {/* QUESTIONS */}
                <FieldArray name="questions">
                  {({ push, remove }) => (
                    <div className="space-y-6">
                      {values.questions.map((q, i) => (
                        <div
                          key={i}
                          className="border rounded-xl p-4 grid gap-4 shadow relative"
                        >
                          <div className="grid gap-2 md:grid-cols-5">
                            {/* QUESTION TEXT */}
                            <div className="grid gap-2 md:col-span-4">
                              <Label>
                                <CustomLabelText
                                  required={true}
                                  labelText={`Question ${i + 1}`}
                                  errorField={errors?.questions?.[i]?.question}
                                />
                              </Label>

                              <Input
                                name={`questions.${i}.question`}
                                value={q.question}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                className={
                                  errors?.questions?.[i]?.question
                                    ? "border-red-500 focus-visible:ring-red-500"
                                    : ""
                                }
                              />
                            </div>

                            {/* QUESTION TYPE SELECT */}
                            <div className="grid gap-2">
                              <Label>
                                <CustomLabelText
                                  required={true}
                                  labelText="Question Type"
                                  errorField={errors?.questions?.[i]?.type}
                                />
                              </Label>

                              <SearchSelect
                                frameworks={[
                                  { label: "Yes / No", value: "0" },
                                  { label: "ABCD Options", value: "1" },
                                ]}
                                className={
                                  errors?.questions?.[i]?.type &&
                                  `focus-visible:ring-red-500 border-red-500`
                                }
                                optionType="Type"
                                initialValue={q.type}
                                disabled={q?.id ? true : false}
                                handleSelect={(val) => {
                                  setFieldValue(`questions.${i}.type`, val);
                                  setFieldValue(`questions.${i}.answer`, "");
                                  if (val === "1") {
                                    setFieldValue(`questions.${i}.options`, [
                                      {
                                        label: "",
                                        text: "",
                                        order: "",
                                      },
                                    ]);
                                  } else {
                                    setFieldValue(`questions.${i}.options`, []);
                                  }
                                }}
                              />
                            </div>
                          </div>

                          {/* YES / NO */}
                          {q.type === "0" && (
                            <div className="grid gap-2">
                              <Label>
                                <CustomLabelText
                                  required={true}
                                  labelText="Correct Answer"
                                  errorField={errors?.questions?.[i]?.answer}
                                />
                              </Label>

                              <SearchSelect
                                frameworks={[
                                  { label: "Yes", value: "Yes" },
                                  { label: "No", value: "No" },
                                ]}
                                className={
                                  errors?.questions?.[i]?.answer &&
                                  `focus-visible:ring-red-500 border-red-500`
                                }
                                optionType="Answer"
                                initialValue={q.answer}
                                handleSelect={(v) =>
                                  setFieldValue(`questions.${i}.answer`, v)
                                }
                              />
                            </div>
                          )}

                          {/* ABCD OPTIONS */}
                          {q.type === "1" && (
                            <FieldArray name={`questions.${i}.options`}>
                              {({ push: addOpt, remove: delOpt }) => (
                                <div className="space-y-4">
                                  {q.options?.map((opt, oi) => (
                                    <div
                                      key={oi}
                                      className="grid gap-3 border p-3 rounded-lg relative"
                                    >
                                      <div className="grid gap-4 md:grid-cols-7">
                                        {/* ORDER */}
                                        <div className="grid gap-2">
                                          <Label>Order</Label>
                                          <Input
                                            type="number"
                                            name={`questions.${i}.options.${oi}.order`}
                                            value={opt.order}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                          />
                                        </div>

                                        {/* LABEL */}
                                        <div className="grid gap-2">
                                          <Label>Label</Label>
                                          <Input
                                            name={`questions.${i}.options.${oi}.label`}
                                            value={opt.label}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                          />
                                        </div>

                                        {/* TEXT */}
                                        <div className="grid gap-2 md:col-span-5">
                                          <Label>Option Text</Label>
                                          <Input
                                            name={`questions.${i}.options.${oi}.text`}
                                            value={opt.text}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                          />
                                        </div>
                                      </div>

                                      <div className=" absolute top-1 right-0">
                                        {oi + 1 === q.options?.length ? (
                                          <>
                                            <Button
                                              type="button"
                                              className="h-fit w-fit p-1 mx-1"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                addOpt({
                                                  label: "",
                                                  text: "",
                                                  order: "",
                                                });
                                              }}
                                            >
                                              <Plus size={16} />
                                            </Button>
                                            {q.options?.length > 1 && (
                                              <Button
                                                type="button"
                                                className="h-fit w-fit p-1 bg-red-500 mx-1"
                                                onClick={() => delOpt(oi)}
                                              >
                                                <Trash2 size={16} />
                                              </Button>
                                            )}{" "}
                                          </>
                                        ) : (
                                          <Button
                                            type="button"
                                            className="h-fit w-fit p-1 bg-red-500 mx-1"
                                            onClick={() => delOpt(oi)}
                                          >
                                            <Trash2 size={16} />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  ))}

                                  {/* Correct Answer */}
                                  <div className="grid gap-2">
                                    <Label>Correct Answer</Label>

                                    <SearchSelect
                                      frameworks={q.options.map((op) => ({
                                        label: op.label,
                                        value: op.label,
                                      }))}
                                      optionType="Answer"
                                      initialValue={q.answer}
                                      handleSelect={(v) =>
                                        setFieldValue(
                                          `questions.${i}.answer`,
                                          v,
                                        )
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                            </FieldArray>
                          )}

                          {/* Action QUESTION */}
                          <div className="absolute top-1 right-0">
                            {i + 1 === values.questions?.length ? (
                              <>
                                <Button
                                  type="button"
                                  className="h-fit w-fit p-1 mx-1"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    push({
                                      question: "",
                                      type: "",
                                      answer: "",
                                      options: [],
                                    });
                                  }}
                                >
                                  <Plus size={16} />
                                </Button>
                                {values.questions?.length > 1 && (
                                  <Button
                                    type="button"
                                    className="h-fit w-fit p-1 bg-red-500 mx-1"
                                    onClick={() => remove(i)}
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                )}
                              </>
                            ) : (
                              <Button
                                type="button"
                                className="h-fit w-fit p-1 bg-red-500 mx-1"
                                onClick={() => remove(i)}
                              >
                                <Trash2 size={16} />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </FieldArray>
              </div>
            </div>
            {/* BUTTONS */}
            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditTraining();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditTraining;
